import React, { useEffect, useState } from "react";
import {
  AccountContent,
  AccountDetails,
  AccountUserInfoContainer,
  BigTitle,
  Button,
  CancelIcon,
  CloseButton,
  ErrorMessage,
  GlobalStyle,
  Logo,
  MainContent,
  ModalButton,
  ModalContent,
  Input as ModalInput,
  ModalLabel,
  ModalOverlay,
  ModalTextArea,
  ModalTitle,
  PageContainer,
  ScrollingAlert,
  Sidebar,
  SubscribeIcon,
  Subtitle,
  SupportIcon,
  Title,
  UserInfoItem
} from "./BetaComponents";

type UserInfo = {
  user_id: string;
  first_name: string;
  last_name: string;
  user_number: string;
  secretary_number: string;
  user_email: string;
  account_page_type: 'welcome' | 'account' | 'resubscribe';
  user_is_subscribed: string | null;
};

const AccountPage = () => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fromEmail, setFromEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [modalError, setModalError] = useState('');
  const [isCanceling, setIsCanceling] = useState(false);

  const personaNameEl = document.getElementById('persona-name');
  let personaName = 'Pip';
  if (personaNameEl) {
    personaName = personaNameEl.textContent?.trim() || 'Pip';
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('canceling') === 'true') {
      setIsCanceling(true);
      setTimeout(() => {
        window.location.href = window.location.pathname;
      }, 3000);
    }

    const userInfoEl = document.getElementById('user-info');
    if (userInfoEl) {
      try {
        const info = JSON.parse(userInfoEl.textContent || '{}');
        setUserInfo(info);
        setFromEmail(info.user_email || '');
      } catch (e) {
        console.error('Failed to parse user info data', e);
      }
    }

    const alertMessageEl = document.getElementById('alert-message');
    if (alertMessageEl) {
      setAlertMessage(alertMessageEl.textContent?.trim() || '');
    }

  }, []);

  const onSubscribe = async () => {
    const response = await fetch("/v0/billing/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (response.ok) {
      const data = await response.json();
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        console.error("No redirect_url found in response");
      }
    } else {
      console.error("Checkout session creation failed", response.status);
    }
  };

  const onCancelSubscription = async () => {
    setIsCanceling(true);
    try {
      const response = await fetch("/v0/billing/subscription/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (response.ok) {
        window.location.href = window.location.pathname + '?canceling=true';
      } else {
        console.error("Failed to cancel subscription", response.status);
        setIsCanceling(false);
      }
    } catch (error) {
      console.error("An error occurred while canceling. Please try again.", error);
      setIsCanceling(false);
    }
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmissionStatus('submitting');
    setModalError('');

    try {
      const formData = new FormData();
      formData.append('to', 'support@call.chat');
      formData.append('from', fromEmail);
      formData.append('subject', subject);
      formData.append('text', description);
      formData.append('headers', 'Message-ID: <call.chat-support-ticket>');

      const response = await fetch('/v0/incoming-email', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit support ticket.');
      }
      setSubmissionStatus('success');
    } catch (error: any) {
      setSubmissionStatus('error');
      setModalError(error.message);
    }
  };

  const openModal = () => {
    setSubmissionStatus('idle');
    setDescription('');
    setModalError('');
    setSubject('');
    if (userInfo) {
      setFromEmail(userInfo.user_email);
    }
    setIsModalOpen(true);
  };

  const renderContent = () => {
    if (!userInfo) {
      return (
        <>
          <BigTitle>Loading...</BigTitle>
          <Subtitle>Just a moment. Your account page is on its way...</Subtitle>
        </>
      );
    }

    switch (userInfo.account_page_type) {
      case 'welcome':
        return (
          <>
            <BigTitle>almost there!</BigTitle>
            <Subtitle>Subscribe to get started. First 30 days are free. Cancel anytime.</Subtitle>
            <Subtitle>{`Once you subscribe, you will receive a text message from ${personaName}, your call.chat assistant!`}<br/>This may take a couple of minutes.</Subtitle>
            <Button onClick={onSubscribe} style={{ maxWidth: '200px', marginTop: '24px' }}>
              Start Free Trial
            </Button>
          </>
        );
      case 'resubscribe':
        return (
          <>
            <BigTitle>we'll miss you!</BigTitle>
            <Subtitle>Your subscription has been cancelled. Resubscribe to continue using our service.</Subtitle>
            <Button onClick={onSubscribe} style={{ maxWidth: '200px', marginTop: '24px' }}>
              Resubscribe
            </Button>
          </>
        );
      case 'account':
        if (isCanceling) {
          return (
          <>
            <BigTitle>Canceling...</BigTitle>
            <Subtitle>Just a moment. We're canceling your subscription now...</Subtitle>
          </>
        );
        }
        return (
          <>
            <AccountContent>
              <AccountUserInfoContainer>
                <h2>Your Account</h2>
                <UserInfoItem label="Name" value={`${userInfo.first_name} ${userInfo.last_name}`} />
                <UserInfoItem label="Email" value={userInfo.user_email || "Not provided"} />
                <UserInfoItem label="Your Number" value={userInfo.user_number} />
                <UserInfoItem label={`${personaName}'s Number`} value={userInfo.secretary_number} />
              </AccountUserInfoContainer>
              <AccountDetails>
                {userInfo.user_is_subscribed ? (
                  <Button onClick={onCancelSubscription}>
                    <CancelIcon style={{ marginRight: '10px' }}/> 
                    Cancel Subscription
                  </Button>
                ) : (
                  <Button onClick={onSubscribe}>
                    <SubscribeIcon style={{ marginRight: '10px' }} /> 
                    Subscribe
                  </Button>
                )}
                <Button onClick={openModal}>
                  <SupportIcon style={{ marginRight: '10px' }} /> 
                  Contact Support
                </Button>
              </AccountDetails>
            </AccountContent>
          </>
        );
      default:
        return (
          <>
            <BigTitle>Loading...</BigTitle>
            <Subtitle>Just a moment. Your account page is on its way...</Subtitle>
          </>
        );
    }
  };

  return (
    <>
      <GlobalStyle />
      <PageContainer>
        <Sidebar>
          <Logo />
        </Sidebar>
        {alertMessage && <ScrollingAlert><p>{alertMessage}</p></ScrollingAlert>}
        <MainContent>
          {renderContent()}
        </MainContent>
      </PageContainer>

      {isModalOpen && (
        <ModalOverlay>
          <ModalContent>
            {submissionStatus === 'success' ? (
              <>
                <ModalTitle>Success!</ModalTitle>
                <p>Thanks, we will get back to you as soon as we can.</p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <CloseButton onClick={() => setIsModalOpen(false)}>Close</CloseButton>
                </div>
              </>
            ) : (
              <form onSubmit={handleModalSubmit}>
                <ModalTitle>Submit a Support Ticket</ModalTitle>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <ModalLabel htmlFor="subject">Subject:</ModalLabel>
                    <ModalInput
                      id="subject"
                      type="text"
                      placeholder="Ticket subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      required
                      modal
                    />
                  </div>
                  <div>
                    <ModalLabel htmlFor="fromEmail">From:</ModalLabel>
                    <ModalInput
                      id="fromEmail"
                      type="email"
                      placeholder="Your email"
                      value={fromEmail}
                      onChange={(e) => setFromEmail(e.target.value)}
                      required
                      modal
                    />
                  </div>
                  <div>
                    <ModalLabel htmlFor="description">Description:</ModalLabel>
                    <ModalTextArea
                      id="description"
                      placeholder="Please be as specific as possible."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      required
                    />
                  </div>
                </div>
                {submissionStatus === 'error' && <ErrorMessage style={{textAlign: 'left', marginTop: '16px'}}>{modalError}</ErrorMessage>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
                  <CloseButton type="button" onClick={() => setIsModalOpen(false)} disabled={submissionStatus === 'submitting'}>
                    Cancel
                  </CloseButton>
                  <ModalButton type="submit" disabled={submissionStatus === 'submitting'}>
                    {submissionStatus === 'submitting' ? 'Submitting...' : 'Submit'}
                  </ModalButton>
                </div>
              </form>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default AccountPage;
